// frontend/src/settings/ShiftPatternSettings.js - 職種フィルター対応版
import React, { useEffect, useState } from "react";
import { manageShiftPatterns } from "../supabase";
import { supabase } from "../supabase";
import { Link } from "react-router-dom";
import { useOrganization } from "../contexts/OrganizationContext";
import { useInternalGuard } from "../internal/useInternalGuard";
import ShiftPatternCsvImportModal from "./ShiftPatternCsvImportModal";

const weekdays = ["月", "火", "水", "木", "金", "土", "日"];

function ShiftPatternSettings() {
  const { currentOrganization } = useOrganization();
  const internalGuard = useInternalGuard();
  
  const [patterns, setPatterns] = useState([]);
  const [skillGroups, setSkillGroups] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [activeTab, setActiveTab] = useState("create");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHolidaySettings, setShowHolidaySettings] = useState(false);
  const [filterJobType, setFilterJobType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({
    name: "",
    displayMark: "",
    targetJobTypes: [],
    startTime: "",
    endTime: "",
    postShiftIds: [],
    forbiddenPostShiftIds: [],
    excludedCountGroups: [],
    countsTowardPatterns: [], // [{ patternId, dayMode: 'all'|'weekdays'|'weekends_holidays' }]
    targetGroup: "",
    requiredCount: {},
    requiredByDayOfWeek: {},
    priority: 1,
    groupName: "",
    ruleBasedOverrides: [],
    forceAssign: false,
    flexibilityType: "flexible",
    flexibilityByDayOfWeek: {},  // 例: { 月: 'fixed', 火: 'flexible' } 未設定の曜日はデフォルト
    minRequired: 0,
    maxConsecutive: 6,
    maxSameConsecutive: 3,
    consecutiveContribution: null,  // 連勤への寄与度 (0/0.5/1)。null は自動判定
    holidayAdjustment: "default",
    holidayCustomCount: 0,
    isChainOnlyPattern: false,
    maxMonthlyRequests: null,
    maxMonthlyRequestsByGroup: [],
    canReceiveRequest: false,
    requestGroup: "",
    requestGroupLabel: "",
    requestDisplayMark: "",
    requestColor: "#757575",
    requestPriority: 999,
    requestRuleComment: "",
    assignmentModeEnabled: false,
  });

  const [editingId, setEditingId] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const getDefaultForm = () => ({
    name: "",
    displayMark: "",
    targetJobTypes: [],
    startTime: "",
    endTime: "",
    postShiftIds: [],
    forbiddenPostShiftIds: [],
    excludedCountGroups: [],
    countsTowardPatterns: [], // [{ patternId, dayMode: 'all'|'weekdays'|'weekends_holidays' }]
    targetGroup: "",
    requiredCount: {},
    requiredByDayOfWeek: {},
    priority: 1,
    groupName: "",
    ruleBasedOverrides: [],
    forceAssign: false,
    flexibilityType: "flexible",
    flexibilityByDayOfWeek: {},  // 例: { 月: 'fixed', 火: 'flexible' } 未設定の曜日はデフォルト
    minRequired: 0,
    maxConsecutive: 6,
    maxSameConsecutive: 3,
    consecutiveContribution: null,  // 連勤への寄与度 (0/0.5/1)。null は自動判定
    holidayAdjustment: "default",
    holidayCustomCount: 0,
    isChainOnlyPattern: false,
    maxMonthlyRequests: null,
    maxMonthlyRequestsByGroup: [],
    canReceiveRequest: false,
    requestGroup: "",
    requestGroupLabel: "",
    requestDisplayMark: "",
    requestColor: "#757575",
    requestPriority: 999,
    requestRuleComment: "",
    assignmentModeEnabled: false,
  });

  const holidayAdjustmentOptions = [
    { value: "default", label: "通常通り(土日と同じ扱い)" },
    { value: "no_assign", label: "祝祭日は割り当てない" },
    { value: "reduce_count", label: "祝祭日は必要人数を30%減らす" },
    { value: "increase_count", label: "祝祭日は必要人数を30%増やす" },
    { value: "custom_count", label: "祝祭日専用の人数を設定" }
  ];

  const fetchPatterns = async (orgId) => {
    try {
      const data = await manageShiftPatterns('list', { organizationId: orgId });
      const patternsData = (data || []).map((pattern) => ({
        id: pattern.id,
        name: pattern.name || pattern.displayMark || '',
        displayMark: pattern.displayMark || pattern.short_name || '',
        targetJobTypes: pattern.targetJobTypes || pattern.target_job_types || pattern.configuration?.target_job_types || [],
        startTime: pattern.startTime || pattern.start_time || '08:30',
        endTime: pattern.endTime || pattern.end_time || '17:00',
        requiredCount: pattern.requiredCount || pattern.required_count || {},
        requiredByDayOfWeek: pattern.requiredByDayOfWeek || pattern.required_by_day_of_week || pattern.requiredCount || pattern.required_count || {},
        priority: pattern.priority || 1,
        flexibilityType: pattern.flexibilityType || pattern.flexibility_type || 'flexible',
        flexibilityByDayOfWeek:
          pattern.flexibilityByDayOfWeek
          ?? pattern.flexibility_by_day_of_week
          ?? pattern.configuration?.flexibility_by_day_of_week
          ?? {},
        minRequired: pattern.minRequired || pattern.min_required || 0,
        forceAssign: pattern.forceAssign || pattern.force_assign || false,
        maxConsecutive: pattern.maxConsecutive || pattern.max_consecutive || 6,
        maxSameConsecutive: pattern.maxSameConsecutive || pattern.max_same_consecutive || 3,
        consecutiveContribution:
          (pattern.consecutiveContribution ?? pattern.consecutive_contribution
            ?? pattern.configuration?.consecutive_contribution) ?? null,
        holidayAdjustment: pattern.holidayAdjustment || pattern.holiday_adjustment || 'default',
        holidayCustomCount: pattern.holidayCustomCount || pattern.holiday_custom_count || 0,
        isChainOnlyPattern: pattern.isChainOnlyPattern || pattern.is_chain_only_pattern || false,
        maxMonthlyRequests: pattern.maxMonthlyRequests || pattern.max_monthly_requests || null,
        maxMonthlyRequestsByGroup: pattern.maxMonthlyRequestsByGroup || pattern.max_monthly_requests_by_group || [],
        canReceiveRequest: pattern.canReceiveRequest || pattern.can_receive_request || false,
        requestGroup: pattern.requestGroup || pattern.request_group || '',
        requestGroupLabel: pattern.requestGroupLabel || pattern.request_group_label || '',
        requestDisplayMark: pattern.requestDisplayMark || pattern.request_display_mark || '',
        requestColor: pattern.requestColor || pattern.request_color || '#757575',
        requestPriority: pattern.requestPriority || pattern.request_priority || 999,
        requestRuleComment: pattern.requestRuleComment || pattern.request_rule_comment || '',
        targetGroup: pattern.targetGroup || pattern.target_group || '',
        postShiftIds: pattern.postShiftIds || pattern.post_shift_ids || [],
        forbiddenPostShiftIds:
          pattern.forbiddenPostShiftIds
          || pattern.forbidden_post_shift_ids
          || pattern.configuration?.forbidden_post_shift_ids
          || [],
        excludedCountGroups:
          pattern.excludedCountGroups
          || pattern.excluded_count_groups
          || pattern.configuration?.excluded_count_groups
          || [],
        countsTowardPatterns:
          pattern.countsTowardPatterns
          || pattern.counts_toward_patterns
          || pattern.configuration?.counts_toward_patterns
          || [],
        ruleBasedOverrides: pattern.ruleBasedOverrides || pattern.rule_based_overrides || [],
        groupName: pattern.groupName || pattern.group_name || '',
        ...pattern.configuration,
        // 🔥 manage-shift-patterns Edge Function は configuration を top-level に
        //    spread して返すので、pattern.configuration はそもそも存在しないことが多い。
        //    consecutiveContribution と同じく top-level / 配下 両方を読む。
        assignmentModeEnabled:
          pattern.assignmentModeEnabled
          ?? pattern.assignment_mode_enabled
          ?? pattern.configuration?.assignmentModeEnabled
          ?? pattern.configuration?.assignment_mode_enabled
          ?? false,
      }));
      setPatterns(patternsData);
    } catch (error) {
      console.error("データ取得エラー:", error);
      alert("データの取得に失敗しました: " + error.message);
    }
  };

  useEffect(() => {
    if (currentOrganization?.id) fetchPatterns(currentOrganization.id);
  }, [currentOrganization]);

  useEffect(() => {
    const fetchSkillGroups = async () => {
      if (!currentOrganization?.id) return;
      try {
        const { data: groups, error } = await supabase
          .from('skill_groups')
          .select('id, name')
          .eq('organization_id', currentOrganization.id);
        if (error) { console.error("スキルグループ取得エラー:", error); return; }
        setSkillGroups(groups || []);
      } catch (error) {
        console.error("スキルグループ取得エラー:", error);
      }
    };
    fetchSkillGroups();
  }, [currentOrganization?.id]);

  useEffect(() => {
    const fetchJobTypes = async () => {
      if (!currentOrganization?.id) return;
      const { data, error } = await supabase
        .from('job_types')
        .select('id, name, display_order')
        .eq('organization_id', currentOrganization.id)
        .order('display_order', { ascending: true });
      if (!error) setJobTypes(data || []);
    };
    fetchJobTypes();
  }, [currentOrganization?.id]);

  const handleChange = (field, value) => {
    if (field === "requiredCount") {
      setForm(prev => ({ ...prev, [field]: value, requiredByDayOfWeek: { ...value } }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    if (!currentOrganization?.id) return;
    // 🔥 自社スタッフは編集モード ON でない限り書き込み拒否 (監査ログにも記録)
    //   通常ユーザーには影響なし (allow が常に true を返す)
    if (!internalGuard.allow('shift_patterns', {
      operation: editingId ? 'update' : 'insert',
      pattern_id: editingId,
      name: form.name,
    })) return;
    try {
      const saveData = {
        name: form.name,
        display_mark: form.displayMark,
        target_job_types: form.targetJobTypes || [],
        start_time: form.startTime || "08:30",
        end_time: form.endTime || "17:00",
        color: "#3b82f6",
        required_count: form.requiredCount || {},
        priority: form.priority || 1,
        required_by_day_of_week: form.requiredByDayOfWeek || form.requiredCount || {},
        flexibility_type: form.flexibilityType || "flexible",
        flexibility_by_day_of_week: (() => {
          // 値が "default" や空の曜日は保存対象から除外（fallback 用）
          const out = {};
          ['月','火','水','木','金','土','日'].forEach((wd) => {
            const v = form.flexibilityByDayOfWeek?.[wd];
            if (v === 'fixed' || v === 'flexible') out[wd] = v;
          });
          return out;
        })(),
        min_required: form.minRequired || 0,
        force_assign: form.forceAssign || false,
        max_consecutive: form.maxConsecutive || 6,
        max_same_consecutive: form.maxSameConsecutive || 3,
        consecutive_contribution:
          form.consecutiveContribution == null || form.consecutiveContribution === ''
            ? null
            : Number(form.consecutiveContribution),
        holiday_adjustment: form.holidayAdjustment || "default",
        holiday_custom_count: form.holidayCustomCount || 0,
        is_chain_only_pattern: form.isChainOnlyPattern || false,
        max_monthly_requests: form.maxMonthlyRequests,
        max_monthly_requests_by_group: form.maxMonthlyRequestsByGroup || [],
        can_receive_request: form.canReceiveRequest || false,
        request_group: form.requestGroup || "",
        request_group_label: form.requestGroupLabel || "",
        request_display_mark: form.requestDisplayMark || "",
        request_color: form.requestColor || "#757575",
        request_priority: form.requestPriority || 999,
        request_rule_comment: form.requestRuleComment || "",
        target_group: form.targetGroup || "",
        post_shift_ids: form.postShiftIds || [],
        forbidden_post_shift_ids: form.forbiddenPostShiftIds || [],
        excluded_count_groups: form.excludedCountGroups || [],
        counts_toward_patterns: form.countsTowardPatterns || [],
        rule_based_overrides: form.ruleBasedOverrides || [],
        group_name: form.groupName || "",
        assignment_mode_enabled: !!form.assignmentModeEnabled,
      };
      if (editingId) {
        await manageShiftPatterns('update', { patternId: editingId, ...saveData, organizationId: currentOrganization.id });
      } else {
        await manageShiftPatterns('add', { ...saveData, organizationId: currentOrganization.id });
      }
      setForm(getDefaultForm());
      setEditingId(null);
      await fetchPatterns(currentOrganization.id);
      setActiveTab("list");
      showMessage(editingId ? "パターンを更新しました" : "新しいパターンを作成しました", "success");
    } catch (error) {
      console.error("保存エラー:", error);
      showMessage("保存に失敗しました: " + error.message, "error");
    }
  };

  const handleEdit = (pattern) => {
    setForm({
      ...getDefaultForm(),
      ...pattern,
      targetJobTypes: pattern.targetJobTypes || [],
      requiredCount: pattern.requiredCount || {},
      requiredByDayOfWeek: pattern.requiredByDayOfWeek || pattern.requiredCount || {},
      ruleBasedOverrides: pattern.ruleBasedOverrides || [],
      postShiftIds: pattern.postShiftIds || [],
      flexibilityType: pattern.flexibilityType || "flexible",
      minRequired: pattern.minRequired || 0,
      forceAssign: pattern.forceAssign || false,
      maxConsecutive: pattern.maxConsecutive || 6,
      maxSameConsecutive: pattern.maxSameConsecutive || 3,
      holidayAdjustment: pattern.holidayAdjustment || "default",
      holidayCustomCount: pattern.holidayCustomCount || 0,
      isChainOnlyPattern: pattern.isChainOnlyPattern || false,
      maxMonthlyRequests: pattern.maxMonthlyRequests || null,
      maxMonthlyRequestsByGroup: pattern.maxMonthlyRequestsByGroup || [],
      canReceiveRequest: pattern.canReceiveRequest || false,
      requestGroup: pattern.requestGroup || "",
      requestGroupLabel: pattern.requestGroupLabel || "",
      requestDisplayMark: pattern.requestDisplayMark || "",
      requestColor: pattern.requestColor || "#757575",
      requestPriority: pattern.requestPriority || 999,
      requestRuleComment: pattern.requestRuleComment || "",
    });
    setEditingId(pattern.id);
    setActiveTab("create");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!currentOrganization?.id) return;
    // 🔥 自社スタッフは編集モード ON でないと削除拒否
    if (!internalGuard.allow('shift_patterns', { operation: 'delete', pattern_id: id })) return;
    if (window.confirm("この勤務パターンを削除しますか？")) {
      try {
        await manageShiftPatterns('delete', { patternId: id, organizationId: currentOrganization.id });
        await fetchPatterns(currentOrganization.id);
        showMessage("パターンを削除しました", "success");
      } catch (error) {
        console.error("削除エラー:", error);
        showMessage("削除に失敗しました: " + error.message, "error");
      }
    }
  };

  const handleRequiredCountChange = (day, value) => {
    const numValue = Number(value);
    const newRequiredCount = { ...form.requiredCount, [day]: numValue };
    setForm(prev => ({ ...prev, requiredCount: newRequiredCount, requiredByDayOfWeek: { ...newRequiredCount } }));
  };

  const showMessage = (text, type = "success") => {
    const message = document.createElement("div");
    message.textContent = text;
    message.style.cssText = `position:fixed;bottom:20px;right:20px;background:${type === "success" ? "#10b981" : "#ef4444"};color:white;padding:1rem 1.5rem;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);z-index:10000;`;
    document.body.appendChild(message);
    setTimeout(() => message.remove(), 3000);
  };

  const getHolidayAdjustmentLabel = (value) => {
    const option = holidayAdjustmentOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  // ✅ フィルタリング (職種フィルタ + 名前/表示記号/希望グループでの検索)
  const normalizedQuery = (searchQuery || '').trim().toLowerCase();
  const filteredPatterns = patterns.filter(p => {
    // 1. 職種フィルタ
    const jobTypeMatch = (() => {
      if (filterJobType === 'all') return true;
      if (filterJobType === 'unset') return !p.targetJobTypes || p.targetJobTypes.length === 0;
      return (p.targetJobTypes || []).includes(filterJobType)
        || !p.targetJobTypes
        || p.targetJobTypes.length === 0;
    })();
    if (!jobTypeMatch) return false;

    // 2. テキスト検索 (勤務名 / 表示記号 / 希望グループ / ラベル を対象)
    if (!normalizedQuery) return true;
    const haystack = [
      p.name,
      p.displayMark,
      p.requestGroup,
      p.requestGroupLabel,
      p.requestDisplayMark,
    ].map(v => (v || '').toLowerCase());
    return haystack.some(v => v.includes(normalizedQuery));
  });
  
  const styles = {
    container: { minHeight: "100vh", backgroundColor: "#f3f4f6", paddingTop: "80px", paddingBottom: "2rem" },
    backLink: { display: "inline-block", marginLeft: "2rem", marginBottom: "1rem", color: "#3b82f6", textDecoration: "none", fontSize: "0.95rem" },
    card: { maxWidth: "1200px", margin: "0 auto", backgroundColor: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "2rem" },
    header: { borderBottom: "2px solid #e5e7eb", paddingBottom: "1rem", marginBottom: "2rem" },
    tabs: { display: "flex", gap: "1rem", marginBottom: "2rem" },
    tab: { padding: "0.75rem 1.5rem", backgroundColor: "#f3f4f6", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "1rem", fontWeight: "500", transition: "all 0.2s" },
    activeTab: { backgroundColor: "#3b82f6", color: "white" },
    formGroup: { marginBottom: "1.5rem" },
    label: { display: "block", marginBottom: "0.5rem", fontWeight: "500", color: "#374151" },
    input: { width: "100%", padding: "0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "1rem", boxSizing: "border-box" },
    select: { width: "100%", padding: "0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "1rem", backgroundColor: "white", cursor: "pointer" },
    checkbox: { marginRight: "0.5rem", width: "18px", height: "18px", cursor: "pointer" },
    checkboxLabel: { display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" },
    row: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" },
    weekdayInputs: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "0.5rem", marginTop: "0.5rem" },
    weekdayInput: { textAlign: "center" },
    weekdayLabel: { display: "block", fontSize: "0.85rem", fontWeight: "500", marginBottom: "0.25rem", color: "#6b7280" },
    smallInput: { width: "100%", padding: "0.5rem", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.9rem" },
    button: { padding: "0.75rem 1.5rem", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "1rem", fontWeight: "500", marginRight: "1rem" },
    cancelButton: { padding: "0.75rem 1.5rem", backgroundColor: "#6b7280", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "1rem", fontWeight: "500" },
    toggleButton: { padding: "0.5rem 1rem", backgroundColor: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem", marginBottom: "1rem" },
    patternList: { display: "flex", flexDirection: "column", gap: "1rem" },
    patternItem: { padding: "1.5rem", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" },
    patternHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" },
    patternInfo: { flex: 1 },
    patternName: { fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937", marginBottom: "0.5rem" },
    patternMeta: { fontSize: "0.9rem", color: "#6b7280", marginBottom: "0.25rem" },
    patternActions: { display: "flex", gap: "0.5rem" },
    editButton: { padding: "0.5rem 1rem", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem" },
    dangerButton: { padding: "0.5rem 1rem", backgroundColor: "#ef4444", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem" },
    infoBox: { backgroundColor: "#e7f3ff", padding: "1rem", borderRadius: "8px", marginBottom: "1.5rem", border: "1px solid #b8daff" },
    infoText: { color: "#004085", margin: 0, fontSize: "0.9rem", lineHeight: "1.6" },
    holidaySection: { backgroundColor: "#fef3c7", padding: "1rem", borderRadius: "8px", marginTop: "1rem", border: "1px solid #fbbf24" },
    holidayTitle: { fontWeight: "600", color: "#92400e", marginBottom: "0.75rem", fontSize: "0.95rem" },
    advancedSection: { backgroundColor: "#f9fafb", padding: "1rem", borderRadius: "8px", marginTop: "1rem", border: "1px solid #e5e7eb" },
    requestSection: { backgroundColor: "#f0f9ff", padding: "1rem", borderRadius: "8px", marginTop: "1rem", border: "1px solid #bae6fd" },
    requestTitle: { fontWeight: "600", color: "#0369a1", marginBottom: "0.75rem", fontSize: "0.95rem" },
    helpText: { fontSize: "0.85rem", color: "#6b7280", marginTop: "0.5rem", fontStyle: "italic" },
  };

  if (!currentOrganization?.id) {
    return (
      <div style={styles.container}>
        <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
        <div style={styles.card}>
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>組織を選択してください</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>勤務パターン設定</h2>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={styles.tabs}>
            <button style={{...styles.tab, ...(activeTab === "create" ? styles.activeTab : {})}} onClick={() => setActiveTab("create")}>
              {editingId ? "編集" : "新規作成"}
            </button>
            <button style={{...styles.tab, ...(activeTab === "list" ? styles.activeTab : {})}} onClick={() => setActiveTab("list")}>
              パターン一覧
            </button>
          </div>
          <button
            type="button"
            onClick={() => setCsvModalOpen(true)}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            📥 CSV 一括登録
          </button>
        </div>

        {activeTab === "create" ? (
          <div>
            <div style={styles.infoBox}>
              <p style={styles.infoText}>勤務パターンを作成します。各パターンには必要人数や優先度を設定できます。</p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>パターン名 *</label>
              <input type="text" style={styles.input} value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="例: 日勤、夜勤、準夜勤" />
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>表示マーク *</label>
                <input type="text" style={styles.input} value={form.displayMark} onChange={(e) => handleChange("displayMark", e.target.value)} placeholder="例: ◯、夜、準" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>優先度</label>
                <input type="number" style={styles.input} value={form.priority} onChange={(e) => handleChange("priority", Number(e.target.value))} min="1" />
                <div style={styles.helpText}>数字が小さいほど優先度が高くなります</div>
              </div>
            </div>

            {/* 人員配置モードで使用するか */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.assignmentModeEnabled}
                  onChange={(e) => handleChange("assignmentModeEnabled", e.target.checked)}
                />
                人員配置モードで使用する（日勤として扱う）
              </label>
              <div style={styles.helpText}>
                ON にすると、このパターンが入っているスタッフは手術配置画面で「日勤者」として候補に出ます。看護師の日勤（例: ◯N）など、職種別の日勤パターンで使用してください。
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>開始時刻</label>
                <input type="time" style={styles.input} value={form.startTime} onChange={(e) => handleChange("startTime", e.target.value)} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>終了時刻</label>
                <input type="time" style={styles.input} value={form.endTime} onChange={(e) => handleChange("endTime", e.target.value)} />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>柔軟性タイプ（デフォルト）</label>
              <select style={styles.select} value={form.flexibilityType} onChange={(e) => handleChange("flexibilityType", e.target.value)}>
                <option value="flexible">柔軟人数</option>
                <option value="fixed">固定人数</option>
              </select>
              <div style={styles.helpText}>固定人数: 必要人数を厳密に守る / 柔軟人数: 最低人数以上であればOK</div>

              {/* 曜日ごとに上書き */}
              <div style={{ marginTop: '0.6rem', padding: '0.6rem', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '6px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
                  📅 曜日ごとに上書き設定（任意）
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  「デフォルトに従う」を選んだ曜日は上のデフォルト設定が適用されます。
                  例: 平日は固定、土日は柔軟 にしたい時など。
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.4rem' }}>
                  {['月','火','水','木','金','土','日'].map((wd) => {
                    const cur = form.flexibilityByDayOfWeek?.[wd] || '';
                    const isWeekend = wd === '土' || wd === '日';
                    return (
                      <div key={wd} style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textAlign: 'center',
                          color: wd === '日' ? '#dc2626' : wd === '土' ? '#2563eb' : '#475569',
                        }}>{wd}</div>
                        <select
                          value={cur}
                          onChange={(e) => {
                            const next = { ...(form.flexibilityByDayOfWeek || {}) };
                            if (e.target.value === '') delete next[wd];
                            else next[wd] = e.target.value;
                            handleChange('flexibilityByDayOfWeek', next);
                          }}
                          style={{
                            padding: '0.3rem 0.2rem',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            background: cur ? (cur === 'fixed' ? '#fef3c7' : '#dbeafe') : 'white',
                            color: cur ? (cur === 'fixed' ? '#92400e' : '#1e40af') : '#94a3b8',
                          }}
                          title={isWeekend ? '土日' : ''}
                        >
                          <option value="">デフォルト</option>
                          <option value="fixed">固定</option>
                          <option value="flexible">柔軟</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {form.flexibilityType === "flexible" && (
              <div style={styles.formGroup}>
                <label style={styles.label}>最低必要人数</label>
                <input type="number" style={styles.input} value={form.minRequired} onChange={(e) => handleChange("minRequired", Number(e.target.value))} min="0" />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>曜日ごとの必要人数</label>
              <div style={styles.weekdayInputs}>
                {weekdays.map(day => (
                  <div key={day} style={styles.weekdayInput}>
                    <label style={styles.weekdayLabel}>{day}</label>
                    <input type="number" style={styles.smallInput} value={form.requiredCount[day] || 0} onChange={(e) => handleRequiredCountChange(day, e.target.value)} min="0" />
                  </div>
                ))}
              </div>
            </div>

            {/* 祝祭日設定 */}
            <div style={styles.formGroup}>
              <button type="button" style={styles.toggleButton} onClick={() => setShowHolidaySettings(!showHolidaySettings)}>
                {showHolidaySettings ? "▼" : "▶"} 祝祭日の動作設定
              </button>
              {showHolidaySettings && (
                <div style={styles.holidaySection}>
                  <div style={styles.holidayTitle}>🎌 祝祭日でのシフト割り当て動作</div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>動作モード</label>
                    <select style={styles.select} value={form.holidayAdjustment} onChange={(e) => handleChange("holidayAdjustment", e.target.value)}>
                      {holidayAdjustmentOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.holidayAdjustment === "custom_count" && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>祝祭日の必要人数</label>
                      <input type="number" style={styles.input} value={form.holidayCustomCount} onChange={(e) => handleChange("holidayCustomCount", Number(e.target.value))} min="0" />
                      <div style={styles.helpText}>祝祭日は曜日設定を無視して、この人数が適用されます</div>
                    </div>
                  )}
                  <div style={styles.infoBox}>
                    <p style={styles.infoText}>💡 祝祭日設定は「設定 → 祝祭日設定」で管理できます。</p>
                  </div>
                </div>
              )}
            </div>

            {/* 詳細設定 */}
            <div style={styles.formGroup}>
              <button type="button" style={styles.toggleButton} onClick={() => setShowAdvanced(!showAdvanced)}>
                {showAdvanced ? "▼" : "▶"} 詳細設定
              </button>
              {showAdvanced && (
                <div style={styles.advancedSection}>
                  <div style={styles.formGroup}>
                    <label style={styles.checkboxLabel}>
                      <input type="checkbox" style={styles.checkbox} checked={form.forceAssign} onChange={(e) => handleChange("forceAssign", e.target.checked)} />
                      強制割り当て (必要人数が0でも最低1人割り当てる)
                    </label>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.checkboxLabel}>
                      <input type="checkbox" style={styles.checkbox} checked={form.isChainOnlyPattern} onChange={(e) => handleChange("isChainOnlyPattern", e.target.checked)} />
                      連鎖専用パターン (単独では割り当てない)
                    </label>
                  </div>

                  <div style={styles.row}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>最大連続勤務日数</label>
                      <input type="number" style={styles.input} value={form.maxConsecutive} onChange={(e) => handleChange("maxConsecutive", Number(e.target.value))} min="1" />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>同一パターン最大連続</label>
                      <input type="number" style={styles.input} value={form.maxSameConsecutive} onChange={(e) => handleChange("maxSameConsecutive", Number(e.target.value))} min="1" />
                    </div>
                  </div>

                  {/* 🆕 連勤への寄与度 */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>連勤への寄与度</label>
                    <select
                      style={styles.input}
                      value={form.consecutiveContribution ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleChange("consecutiveContribution", v === '' ? null : Number(v));
                      }}
                    >
                      <option value="">自動判定（休/有給/夏休み/明け=0、その他=1）</option>
                      <option value="0">0（連勤を切る - 休み扱い）</option>
                      <option value="0.5">0.5（半日勤務 - 例: 午前・午後）</option>
                      <option value="1">1（通常勤務 - 1日分）</option>
                    </select>
                    <div style={styles.helpText}>
                      ⚙️ 連勤計算でこのパターンが何日分の勤務として数えられるか。<br />
                      例: 「日勤×5 + 午前(0.5)」= 5.5連勤。最大連勤上限と比較されます。<br />
                      <strong>0</strong> にすると休み扱いで連勤が切れます。空欄なら従来通り自動判定。
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>対象スキルグループ</label>
                    <select style={styles.select} value={form.targetGroup} onChange={(e) => handleChange("targetGroup", e.target.value)}>
                      <option value="">指定なし（全員対象）</option>
                      {skillGroups.map(group => (
                        <option key={group.id} value={group.name}>{group.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* ✅ 対象職種 */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>対象職種</label>
                    <div style={styles.helpText}>未選択の場合は全職種対象になります</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {jobTypes.map(jt => {
                        const isSelected = (form.targetJobTypes || []).includes(jt.id);
                        return (
                          <label key={jt.id} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer',
                            border: isSelected ? '2px solid #6366f1' : '1px solid #e5e7eb',
                            backgroundColor: isSelected ? '#e0e7ff' : 'white',
                            fontWeight: isSelected ? '600' : '400',
                            fontSize: '0.9rem', color: isSelected ? '#4338ca' : '#374151',
                            transition: 'all 0.2s',
                          }}>
                            <input type="checkbox" checked={isSelected}
                              onChange={(e) => {
                                const current = form.targetJobTypes || [];
                                handleChange('targetJobTypes', e.target.checked
                                  ? [...current, jt.id]
                                  : current.filter(id => id !== jt.id)
                                );
                              }}
                              style={{ display: 'none' }}
                            />
                            {jt.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>後続シフトパターン</label>
                    <select multiple style={{...styles.select, height: "120px"}} value={form.postShiftIds}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        handleChange("postShiftIds", selected);
                      }}>
                      {patterns.filter(p => p.id !== editingId).map(pattern => (
                        <option key={pattern.id} value={pattern.id}>{pattern.name} ({pattern.displayMark})</option>
                      ))}
                    </select>
                    <div style={styles.helpText}>Ctrl/Cmdキーを押しながらクリックで複数選択。このパターンの翌日に自動配置される（明け等）</div>
                  </div>

                  {/* 🔥 後続禁止パターン: このパターンの翌日に置いてはいけない勤務 */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      🚫 後続禁止パターン
                      <span style={{ fontSize: '10px', color: '#dc2626', marginLeft: '6px', fontWeight: 400 }}>
                        （翌日に配置NG）
                      </span>
                    </label>
                    <select multiple style={{...styles.select, height: "100px", borderColor: '#fca5a5'}}
                      value={form.forbiddenPostShiftIds || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        handleChange("forbiddenPostShiftIds", selected);
                      }}>
                      {patterns.filter(p => p.id !== editingId).map(pattern => (
                        <option key={pattern.id} value={pattern.id}>{pattern.name} ({pattern.displayMark})</option>
                      ))}
                    </select>
                    <div style={styles.helpText}>
                      Ctrl/Cmdキーを押しながらクリックで複数選択。例:「夜勤」に「日勤」を設定すると、夜勤の翌日には日勤が配置されません。
                    </div>
                  </div>

                  {/* 🔥 固定人数にカウントしないスキルグループ */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      🧑‍🎓 必要人数カウント対象外グループ
                      <span style={{ fontSize: '10px', color: '#0891b2', marginLeft: '6px', fontWeight: 400 }}>
                        （新人・研修中等）
                      </span>
                    </label>
                    <select multiple style={{...styles.select, height: "100px", borderColor: '#67e8f9'}}
                      value={form.excludedCountGroups || []}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        handleChange("excludedCountGroups", selected);
                      }}>
                      {skillGroups.map(group => (
                        <option key={group.id} value={group.name}>{group.name}</option>
                      ))}
                    </select>
                    <div style={styles.helpText}>
                      Ctrl/Cmdキーを押しながらクリックで複数選択。
                      ここに指定したスキルグループのメンバーがこの勤務に配置されても「必要人数」に数えません。
                      <br />例: 研修中の新人グループを指定 → 新人を入れても戦力としてカウントしないので、
                      別途必要人数分の指導者層が配置されます。
                    </div>
                  </div>

                  {/* 🔥 他パターンの必要人数にカウントする設定 */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      🔢 他パターンの必要人数にカウント
                      <span style={{ fontSize: '10px', color: '#0891b2', marginLeft: '6px', fontWeight: 400 }}>
                        （オンコール明け→日勤としてカウント等）
                      </span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto', border: '1px solid #67e8f9', borderRadius: '6px', padding: '8px' }}>
                      {patterns.filter(p => p.id !== editingId).length === 0 && (
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>他のパターンがありません</div>
                      )}
                      {patterns.filter(p => p.id !== editingId).map(targetPat => {
                        const list = form.countsTowardPatterns || [];
                        const entry = list.find(x => x.patternId === targetPat.id);
                        const enabled = !!entry;
                        const dayMode = entry?.dayMode || 'all';
                        return (
                          <div key={targetPat.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '220px' }}>
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => {
                                  const next = (form.countsTowardPatterns || []).filter(x => x.patternId !== targetPat.id);
                                  if (e.target.checked) next.push({ patternId: targetPat.id, dayMode: 'all' });
                                  handleChange('countsTowardPatterns', next);
                                }}
                              />
                              <span>{targetPat.name} ({targetPat.displayMark})</span>
                            </label>
                            <select
                              disabled={!enabled}
                              value={dayMode}
                              style={{ ...styles.select, height: '28px', padding: '2px 6px', fontSize: '12px', minWidth: '140px', opacity: enabled ? 1 : 0.4 }}
                              onChange={(e) => {
                                const next = (form.countsTowardPatterns || []).map(x =>
                                  x.patternId === targetPat.id ? { ...x, dayMode: e.target.value } : x
                                );
                                handleChange('countsTowardPatterns', next);
                              }}
                            >
                              <option value="all">全日</option>
                              <option value="weekdays">平日のみ</option>
                              <option value="weekends_holidays">土日祝のみ</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                    <div style={styles.helpText}>
                      指定したパターンの「必要人数」にこのパターン配置者もカウントされます。
                      <br />例: 「オンコール明け」で「日勤」を指定 + 平日のみ → 平日のオンコール明けの人を日勤の必要人数に加算。
                      <br />自動割り当てロジックもこの設定を考慮します。
                    </div>
                  </div>

                  {/* 希望受付設定 */}
                  <div style={styles.requestSection}>
                    <div style={styles.requestTitle}>📝 希望受付設定</div>
                    <div style={styles.formGroup}>
                      <label style={styles.checkboxLabel}>
                        <input type="checkbox" style={styles.checkbox} checked={form.canReceiveRequest} onChange={(e) => handleChange("canReceiveRequest", e.target.checked)} />
                        このパターンで希望を受け付ける
                      </label>
                    </div>
                    {form.canReceiveRequest && (
                      <>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>希望グループID *</label>
                          <input type="text" style={styles.input} value={form.requestGroup} onChange={(e) => handleChange("requestGroup", e.target.value)} placeholder="例: night, day, off" />
                          <div style={styles.helpText}>同じIDの勤務パターンは1つの希望としてまとめられます</div>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>希望フォームでの表示名 *</label>
                          <input type="text" style={styles.input} value={form.requestGroupLabel} onChange={(e) => handleChange("requestGroupLabel", e.target.value)} placeholder="例: 夜勤希望、日勤希望" />
                        </div>
                        <div style={styles.row}>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>希望フォームでの記号</label>
                            <input type="text" style={styles.input} value={form.requestDisplayMark} onChange={(e) => handleChange("requestDisplayMark", e.target.value)} placeholder="例: 夜、日、休" maxLength={2} />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>希望フォームでの色</label>
                            <input type="color" style={{...styles.input, height: "45px"}} value={form.requestColor} onChange={(e) => handleChange("requestColor", e.target.value)} />
                          </div>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>希望フォームでの表示順序</label>
                          <input type="number" style={styles.input} value={form.requestPriority} onChange={(e) => handleChange("requestPriority", Number(e.target.value))} min="1" />
                          <div style={styles.helpText}>数字が小さいほど上に表示されます</div>
                        </div>
                        <div style={styles.formGroup}>
                          <label style={styles.label}>月間希望受付回数の上限</label>
                          <input type="number" min="0" style={styles.input} value={form.maxMonthlyRequests || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              handleChange("maxMonthlyRequests", val === "" || val === "0" ? null : Number(val));
                            }}
                            placeholder="空欄で無制限"
                          />
                          <div style={{ marginTop: "0.75rem" }}>
                            <label style={styles.label}>提出ルールのコメント（任意）</label>
                            <input type="text" style={styles.input} value={form.requestRuleComment || ""} onChange={(e) => handleChange("requestRuleComment", e.target.value)} placeholder="例: 希望が多い場合は調整させていただきます" />
                            <div style={styles.helpText}>シフト提出画面の「提出ルール」に表示されます</div>
                          </div>

                          {/* スキルグループごとの上限 */}
                          <div style={styles.formGroup}>
                            <label style={styles.checkboxLabel}>
                              <input type="checkbox" style={styles.checkbox}
                                checked={(form.maxMonthlyRequestsByGroup?.length || 0) > 0}
                                onChange={(e) => { if (!e.target.checked) handleChange("maxMonthlyRequestsByGroup", []); }}
                              />
                              スキルグループごとに異なる上限を設定
                            </label>
                            {form.maxMonthlyRequestsByGroup !== undefined && form.maxMonthlyRequestsByGroup !== null && (
                              <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                <div style={styles.helpText}>💡 各スキルグループに異なる上限を設定できます。複数グループに属するスタッフは最も緩い上限が適用されます。</div>
                                <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.5rem", marginBottom: "1rem" }}>
                                  {skillGroups.map(group => {
                                    const isSelected = form.maxMonthlyRequestsByGroup.some(item => item.skillGroupId === group.id);
                                    return (
                                      <label key={group.id} style={{ display: "flex", alignItems: "center", padding: "0.5rem", backgroundColor: isSelected ? "#dbeafe" : "white", borderRadius: "6px", border: isSelected ? "2px solid #3b82f6" : "1px solid #e5e7eb", cursor: "pointer" }}>
                                        <input type="checkbox" checked={isSelected}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              handleChange("maxMonthlyRequestsByGroup", [...form.maxMonthlyRequestsByGroup, { skillGroupId: group.id, skillGroupName: group.name, maxRequests: form.maxMonthlyRequests || 3, comment: "" }]);
                                            } else {
                                              handleChange("maxMonthlyRequestsByGroup", form.maxMonthlyRequestsByGroup.filter(item => item.skillGroupId !== group.id));
                                            }
                                          }}
                                          style={{ marginRight: "0.5rem" }}
                                        />
                                        <span style={{ fontSize: "0.9rem", fontWeight: isSelected ? "600" : "400" }}>{group.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                                {form.maxMonthlyRequestsByGroup.length > 0 && form.maxMonthlyRequestsByGroup.map((item, idx) => (
                                  <div key={item.skillGroupId} style={{ marginBottom: "0.75rem", padding: "0.75rem", backgroundColor: "white", borderRadius: "6px", border: "1px solid #e5e7eb" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
                                      <span style={{ flex: "0 0 auto", fontWeight: "500", minWidth: "120px" }}>{item.skillGroupName}</span>
                                      <span style={{ fontSize: "0.9rem", color: "#6b7280" }}>月</span>
                                      <input type="number" min="0" style={{ ...styles.input, width: "80px", textAlign: "center" }} value={item.maxRequests}
                                        onChange={(e) => {
                                          const newList = [...form.maxMonthlyRequestsByGroup];
                                          newList[idx].maxRequests = parseInt(e.target.value) || 0;
                                          handleChange("maxMonthlyRequestsByGroup", newList);
                                        }}
                                      />
                                      <span style={{ fontSize: "0.9rem", color: "#6b7280" }}>回まで</span>
                                    </div>
                                    <input type="text" style={{ ...styles.input, fontSize: "0.9rem", padding: "0.5rem" }} value={item.comment || ""}
                                      onChange={(e) => {
                                        const newList = [...form.maxMonthlyRequestsByGroup];
                                        newList[idx].comment = e.target.value;
                                        handleChange("maxMonthlyRequestsByGroup", newList);
                                      }}
                                      placeholder="コメント（任意）"
                                    />
                                  </div>
                                ))}
                                <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "6px", fontSize: "0.85rem", color: "#78350f" }}>
                                  ⚠️ 選択されていないグループには基本上限（{form.maxMonthlyRequests || "無制限"}）が適用されます
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={styles.infoBox}>
                          <p style={styles.infoText}>
                            💡 <strong>希望受付の仕組み:</strong><br/>
                            同じ「希望グループID」を持つ複数の勤務パターンは、希望フォームでは1つの選択肢としてまとめられます。
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <button style={styles.button} onClick={handleSave}>{editingId ? "更新" : "作成"}</button>
              <button style={styles.cancelButton} onClick={() => { setForm(getDefaultForm()); setEditingId(null); setActiveTab("list"); }}>キャンセル</button>
            </div>
          </div>
        ) : (
          <div>
            {/* 🔍 検索ボックス */}
            <div style={{ marginBottom: '1rem', position: 'relative' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="勤務名 / 表示記号 / 希望グループで検索"
                style={{
                  width: '100%',
                  padding: '0.6rem 2.4rem 0.6rem 2.2rem',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: '0.9rem',
                  boxSizing: 'border-box',
                }}
              />
              <span style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: '#9ca3af', fontSize: '1rem', pointerEvents: 'none',
              }}>🔍</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#6b7280', fontSize: '1.1rem', padding: '0 6px',
                  }}
                  title="クリア"
                >
                  ×
                </button>
              )}
            </div>

            {/* ✅ 職種フィルター */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
              <button onClick={() => setFilterJobType('all')} style={{
                padding: '0.4rem 1rem', borderRadius: '20px', border: 'none', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: filterJobType === 'all' ? '700' : '400',
                backgroundColor: filterJobType === 'all' ? '#1f2937' : '#f3f4f6',
                color: filterJobType === 'all' ? 'white' : '#374151',
              }}>すべて（{patterns.length}）</button>
              <button onClick={() => setFilterJobType('unset')} style={{
                padding: '0.4rem 1rem', borderRadius: '20px', border: 'none', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: filterJobType === 'unset' ? '700' : '400',
                backgroundColor: filterJobType === 'unset' ? '#6b7280' : '#f3f4f6',
                color: filterJobType === 'unset' ? 'white' : '#374151',
              }}>全職種共通（{patterns.filter(p => !p.targetJobTypes || p.targetJobTypes.length === 0).length}）</button>
              {jobTypes.map(jt => (
  <button key={jt.id} onClick={() => setFilterJobType(jt.id)} style={{
    padding: '0.4rem 1rem', borderRadius: '20px', border: 'none', cursor: 'pointer',
    fontSize: '0.85rem', fontWeight: filterJobType === jt.id ? '700' : '400',
    backgroundColor: filterJobType === jt.id ? '#6366f1' : '#f3f4f6',
    color: filterJobType === jt.id ? 'white' : '#374151',
  }}>{jt.name}（{patterns.filter(p => 
    (p.targetJobTypes || []).includes(jt.id) || 
    !p.targetJobTypes || 
    p.targetJobTypes.length === 0
  ).length}）</button>
))}
            </div>

            {filteredPatterns.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
                {searchQuery
                  ? `「${searchQuery}」に該当するパターンがありません`
                  : (filterJobType === 'all' ? '勤務パターンが登録されていません' : '該当するパターンがありません')}
              </div>
            ) : (
              <div style={styles.patternList}>
                {filteredPatterns.map(pattern => (
                  <div key={pattern.id} style={styles.patternItem}>
                    <div style={styles.patternHeader}>
                      <div style={styles.patternInfo}>
                        <div style={styles.patternName}>{pattern.name} ({pattern.displayMark})</div>
                        <div style={styles.patternMeta}>
                          優先度: {pattern.priority} | {pattern.flexibilityType === "fixed" ? " 固定人数" : ` 柔軟人数（最低${pattern.minRequired || 0}人）`}
                          {pattern.forceAssign && " | 強制割当"}
                        </div>
                        {pattern.targetJobTypes && pattern.targetJobTypes.length > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6366f1' }}>
                            👔 対象職種: {pattern.targetJobTypes.map(id => jobTypes.find(j => j.id === id)?.name).filter(Boolean).join(', ')}
                          </div>
                        )}
                        {pattern.assignmentModeEnabled && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#15803d', fontWeight: 600 }}>
                            🎯 人員配置モードで使用（日勤として扱う）
                          </div>
                        )}
                        {pattern.canReceiveRequest && (
                          <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "#0369a1" }}>
                            📝 希望受付: {pattern.requestGroupLabel || pattern.requestGroup}
                            {pattern.maxMonthlyRequests && ` (月${pattern.maxMonthlyRequests}回まで)`}
                            {pattern.requestRuleComment && <span style={{ marginLeft: "0.5rem", color: "#6b7280" }}>- {pattern.requestRuleComment}</span>}
                          </div>
                        )}
                        {!pattern.isChainOnlyPattern && (
                          <div style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                            必要人数: {weekdays.map(day => (pattern.requiredCount?.[day] || 0) > 0 ? `${day}:${pattern.requiredCount[day]}` : null).filter(Boolean).join(", ") || "設定なし"}
                          </div>
                        )}
                        {pattern.holidayAdjustment && pattern.holidayAdjustment !== "default" && (
                          <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "#92400e" }}>
                            🎌 祝祭日: {getHolidayAdjustmentLabel(pattern.holidayAdjustment)}
                            {pattern.holidayAdjustment === "custom_count" && ` (${pattern.holidayCustomCount}人)`}
                          </div>
                        )}
                        {pattern.postShiftIds && pattern.postShiftIds.length > 0 && (
                          <div style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>
                            後続: {pattern.postShiftIds.map(id => patterns.find(p => p.id === id)?.name).filter(Boolean).join(", ")}
                          </div>
                        )}
                      </div>
                      <div style={styles.patternActions}>
                        <button onClick={() => handleEdit(pattern)} style={styles.editButton}>編集</button>
                        <button onClick={() => handleDelete(pattern.id)} style={styles.dangerButton}>削除</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV 一括登録モーダル */}
      <ShiftPatternCsvImportModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        onSuccess={() => { if (currentOrganization?.id) fetchPatterns(currentOrganization.id); }}
        organizationId={currentOrganization?.id}
        existingNames={patterns.map(p => p.name).filter(Boolean)}
      />
    </div>
  );
}

export default ShiftPatternSettings;